'use strict'

import cloneDeep from 'lodash/cloneDeep'
import {
  recordSettlement as apiRecordSettlement,
  updateSettlement as apiUpdateSettlement,
} from '@/api/refinery/upstreamMasterLotMockApi'
import {
  buildNetsuiteSettlementEnvelope,
  applyNetsuiteSalesOrderSnapshot,
  isCashInvoiceDisposition,
  NETSUITE_DOCUMENT_SOURCE,
  NETSUITE_OUTBOUND_KINDS,
  NETSUITE_PAYMENT_STATUS,
  netsuiteSettlementFingerprint,
  normalizeNetsuiteSettlementEnvelope,
  settlementInputFromPersistedSettlement,
  settlementInputFromNetsuiteEnvelope,
  validateNetsuiteSettlementEnvelope,
} from '@/util/refinery/upstream/upstreamMasterLot'
import {MASTER_LOT_STATUSES} from '@/util/refinery/upstream/constants'
import {
  isHttpsUrl,
  isIsoDateTime,
  normalizeNetsuiteTransactionIdentity,
} from '@/util/netsuiteLinks'

const NETSUITE_DOCUMENT_STORAGE_STATUS = 'copied'

export const createUpstreamMasterLotNetsuitePrototypeAdapter = ({
  sendSettlement,
  recordSettlement,
  updateSettlement,
  now,
} = {}) => {
  assertCollaborator('sendSettlement', sendSettlement)
  assertCollaborator('recordSettlement', recordSettlement)
  assertCollaborator('updateSettlement', updateSettlement)
  assertCollaborator('now', now)

  const simulateSettlementOutbound = async (masterLot) => {
    const envelope = buildNetsuiteSettlementEnvelope(masterLot)
    await sendSettlement(envelope)
    return changedResult(recordOutbound(masterLot, envelope, now(), NETSUITE_OUTBOUND_KINDS.PG_ORIGIN))
  }

  const simulateNetsuiteSettlementIngress = async (masterLot, envelope) => {
    assertValidSettlementIngress(masterLot, envelope)
    const normalized = normalizeNetsuiteSettlementEnvelope(envelope)
    const fingerprint = netsuiteSettlementFingerprint(normalized)
    if (lastInboundFingerprint(masterLot) === fingerprint) {
      return {lot: masterLot, changed: false}
    }
    const input = settlementInputFromNetsuiteEnvelope(normalized)
    const persisted = await persistInboundSettlement(masterLot, input, {
      recordSettlement,
      updateSettlement,
    })
    const receivedAt = now()
    const withInbound = recordInbound(persisted, {fingerprint, receivedAt})
    await sendSettlement(normalized)
    return changedResult(recordOutbound(
      withInbound,
      normalized,
      receivedAt,
      NETSUITE_OUTBOUND_KINDS.NS_SEND_BACK,
    ))
  }

  const simulateNetsuiteInvoicePaidIngress = async (masterLot, event) => {
    assertSettledLot(masterLot, 'invoice payment')
    if (!isCashInvoiceDisposition(masterLot.settlement?.dispositionMode)) {
      throw new Error('invoice payment requires a cash invoice settlement')
    }
    assertInvoicePaidEvent(event)
    const invoice = normalizeInvoicePaidIdentity(event)
    return changedResult(withNetsuiteSync(masterLot, {
      invoice,
    }, now()))
  }

  const simulateNetsuiteFileLinkIngress = async (masterLot, event) => {
    assertSettledLot(masterLot, 'file link')
    assertFileLinkEvent(event)
    const inboundDocument = inboundDocumentFrom(event)
    const existingDocument = (masterLot.settlement.documents || [])
      .find((document) => document?.id === inboundDocument.id)
    if (existingDocument) {
      if (sameInboundDocument(existingDocument, inboundDocument)) {
        return {lot: masterLot, changed: false}
      }
      throw new Error(`NetSuite document ${inboundDocument.id} conflicts with existing settlement document`)
    }
    const input = settlementInputFromPersistedSettlement(masterLot)
    input.documents = [
      ...(masterLot.settlement.documents || []).map(cloneDeep),
      inboundDocument,
    ]
    const persisted = await updateSettlement(masterLot, input)
    return changedResult({...persisted, updatedAt: now()})
  }

  const simulateNetsuiteSalesOrderSyncIngress = async (masterLot, event) => {
    return applyNetsuiteSalesOrderSnapshot(masterLot, event)
  }

  return Object.freeze({
    simulateSettlementOutbound,
    simulateNetsuiteSettlementIngress,
    simulateNetsuiteInvoicePaidIngress,
    simulateNetsuiteFileLinkIngress,
    simulateNetsuiteSalesOrderSyncIngress,
  })
}

const persistInboundSettlement = (masterLot, input, collaborators) => {
  if (masterLot.status === MASTER_LOT_STATUSES.ASSAYED) {
    return collaborators.recordSettlement(masterLot, input)
  }
  if (masterLot.status === MASTER_LOT_STATUSES.SETTLED) {
    return collaborators.updateSettlement(masterLot, input)
  }
  throw new Error(`NetSuite settlement ingress requires assayed or settled lot (currently: ${masterLot.status})`)
}

const assertValidSettlementIngress = (masterLot, envelope) => {
  if (!masterLot?.id) throw new Error('master lot required')
  const errors = validateNetsuiteSettlementEnvelope(envelope, masterLot)
  if (errors.length) throw new Error(errors.join('; '))
}

const recordInbound = (masterLot, lastInbound) => withNetsuiteSync(masterLot, {
  settlement: {
    ...(masterLot.netsuiteSync?.settlement || {}),
    lastInbound,
  },
}, lastInbound.receivedAt)

const recordOutbound = (masterLot, envelope, sentAt, kind) => withNetsuiteSync(masterLot, {
  settlement: {
    ...(masterLot.netsuiteSync?.settlement || {}),
    lastOutbound: {
      status: 'sent',
      kind,
      sentAt,
      envelope: cloneDeep(envelope),
    },
  },
}, sentAt)

const withNetsuiteSync = (masterLot, patch, updatedAt) => ({
  ...cloneDeep(masterLot),
  netsuiteSync: mergeNetsuiteSync(masterLot.netsuiteSync, patch),
  updatedAt,
})

const mergeNetsuiteSync = (existing, patch) => ({
  ...(cloneDeep(existing) || {}),
  ...cloneDeep(patch),
  settlement: patch.settlement
    ? {...(cloneDeep(existing?.settlement) || {}), ...cloneDeep(patch.settlement)}
    : cloneDeep(existing?.settlement),
  salesOrder: patch.salesOrder
    ? {...(cloneDeep(existing?.salesOrder) || {}), ...cloneDeep(patch.salesOrder)}
    : cloneDeep(existing?.salesOrder),
})

const lastInboundFingerprint = (masterLot) =>
  masterLot.netsuiteSync?.settlement?.lastInbound?.fingerprint || null

const inboundDocumentFrom = (event) => ({
  id: event.documentId,
  filename: event.filename,
  mimeType: event.mimeType,
  byteSize: event.byteSize,
  caption: event.caption || '',
  uploadedAt: event.receivedAt,
  source: NETSUITE_DOCUMENT_SOURCE,
  sourceUrl: event.sourceUrl,
  storageStatus: NETSUITE_DOCUMENT_STORAGE_STATUS,
})

const sameInboundDocument = (existing, incoming) => [
  'id',
  'filename',
  'mimeType',
  'byteSize',
  'caption',
  'source',
  'sourceUrl',
  'storageStatus',
].every((field) => existing?.[field] === incoming[field])

const assertSettledLot = (masterLot, operation) => {
  if (!masterLot?.id || masterLot.status !== MASTER_LOT_STATUSES.SETTLED || !masterLot.settlement) {
    throw new Error(`NetSuite ${operation} requires a settled lot`)
  }
}

const assertInvoicePaidEvent = (event) => {
  if (!/^\d+$/.test(String(event?.netsuite_internalId || ''))) {
    throw new Error('numeric invoice netsuite_internalId required')
  }
  if (!nonEmptyString(event?.tranId)) throw new Error('invoice tranId required')
  if (String(event.netsuite_internalId) === event.tranId) {
    throw new Error('invoice internal id must differ from tranId')
  }
  if (!isHttpsUrl(event?.url)) throw new Error('invoice url must be a valid HTTPS URL')
  if (!isIsoDateTime(event?.paidAt)) throw new Error('paidAt must be a valid ISO timestamp')
}

const normalizeInvoicePaidIdentity = (event) => ({
  ...normalizeNetsuiteTransactionIdentity({
    netsuite_internalId: event.netsuite_internalId,
    tranId: event.tranId,
    status: NETSUITE_PAYMENT_STATUS,
  }, new URL(event.url).origin),
  paidAt: event.paidAt,
})

const assertFileLinkEvent = (event) => {
  if (!nonEmptyString(event?.documentId)) throw new Error('documentId required')
  if (!nonEmptyString(event?.filename)) throw new Error('filename required')
  if (!nonEmptyString(event?.mimeType)) throw new Error('mimeType required')
  if (!Number.isFinite(event?.byteSize) || event.byteSize < 0) {
    throw new Error('byteSize must be finite and non-negative')
  }
  if (!isHttpsUrl(event?.sourceUrl)) throw new Error('sourceUrl must be a valid HTTPS URL')
  if (!isIsoDateTime(event?.receivedAt)) throw new Error('receivedAt must be a valid ISO timestamp')
}

const assertCollaborator = (name, collaborator) => {
  if (typeof collaborator !== 'function') throw new Error(`${name} collaborator required`)
}

const changedResult = (lot) => ({lot, changed: true})
const nonEmptyString = (value) => typeof value === 'string' && value.trim().length > 0
const simulateSettlementTransport = async () => undefined

export const upstreamMasterLotNetsuitePrototypeAdapter = createUpstreamMasterLotNetsuitePrototypeAdapter({
  sendSettlement: simulateSettlementTransport,
  recordSettlement: apiRecordSettlement,
  updateSettlement: apiUpdateSettlement,
  now: () => new Date().toISOString(),
})
